import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clapperboard,
  Languages,
  LayoutTemplate,
  Megaphone,
  MonitorPlay,
  MousePointerClick,
  Palette,
  Shuffle,
  Search,
  UserRound,
  Video,
  Youtube,
} from 'lucide-react';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import { TEMPLATE_CATALOG, TEMPLATE_CATEGORIES } from './templateCatalog';
import './TemplatesPage.css';

const TEMPLATE_ICON_BY_ID = {
  'ugc-ads': Megaphone,
  'localize-ad': Languages,
  'hook-changer': Shuffle,
  'change-avatar': UserRound,
  'change-style': Palette,
  'change-cta': MousePointerClick,
  'zack-d-style-shorts': Clapperboard,
  'fern-style-youtube-video': Youtube,
  'saas-product-demo': MonitorPlay,
  'studio-talking-head': Video,
};

function getTemplateIcon(templateId) {
  return TEMPLATE_ICON_BY_ID[templateId] || LayoutTemplate;
}

function TemplateGalleryCard({ template, onSelect }) {
  const Icon = getTemplateIcon(template.id);

  return (
    <button
      type="button"
      className={`templates-card templates-card--${template.accent}`}
      onClick={() => onSelect(template)}
    >
      <span className="templates-card__preview" aria-hidden="true">
        <span className="templates-card__preview-grid">
          <span />
          <span />
          <span />
          <span />
        </span>
        <span className="templates-card__preview-icon">
          <Icon size={24} strokeWidth={1.7} />
        </span>
        <span className="templates-card__badge">{template.shortTitle}</span>
      </span>
      <span className="templates-card__content">
        <span className="templates-card__title">{template.title}</span>
        <span className="templates-card__desc">{template.description}</span>
        <span className="templates-card__meta">
          <span>{template.estimate}</span>
          <span>{template.credits}</span>
        </span>
      </span>
    </button>
  );
}

function TemplatesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const filteredTemplates = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return TEMPLATE_CATALOG.filter((template) => {
      const matchesCategory = category === 'all' || template.category === category;
      if (!matchesCategory) return false;
      if (!normalizedSearch) return true;
      return [
        template.title,
        template.description,
        template.summary,
        template.categoryLabel,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [category, search]);

  const handleSelectTemplate = (template) => {
    if (template?.route) navigate(template.route);
  };

  return (
    <AuthenticatedLayout>
      <div className="templates-page">
        <section className="templates-library" aria-labelledby="templates-library-title">
          <div className="templates-library__toolbar">
            <div>
              <h1 id="templates-library-title" className="templates-library__title">
                Template Library
              </h1>
              <p className="templates-library__count">
                {filteredTemplates.length} template{filteredTemplates.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>

          <div className="templates-tabs" aria-label="Template categories">
            {TEMPLATE_CATEGORIES.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`templates-tab${category === item.id ? ' templates-tab--active' : ''}`}
                onClick={() => setCategory(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="templates-search-row">
            <label className="templates-search">
              <Search size={18} strokeWidth={1.8} aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search templates..."
              />
            </label>
          </div>

          {filteredTemplates.length > 0 ? (
            <div className="templates-grid">
              {filteredTemplates.map((template) => (
                <TemplateGalleryCard
                  key={template.id}
                  template={template}
                  onSelect={handleSelectTemplate}
                />
              ))}
            </div>
          ) : (
            <div className="templates-empty">
              <h3>No templates found</h3>
              <p>Try a different search or category.</p>
            </div>
          )}
        </section>
      </div>
    </AuthenticatedLayout>
  );
}

export default TemplatesPage;
